'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const capstateFeatures = [
    {
        title: 'Granular Code Restore',
        desc: 'Restore a single function, block, or line from any point in your project history — without touching the rest of your file.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 10a6 6 0 0112 0" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M4 10l-2-2m2 2l2-2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6 14h8M8 17h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>

    },
    {
        title: 'Function-level History',
        desc: 'Every function in your codebase has its own timeline. Navigate changes at the function level, not just the commit level.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 3v14M10 6v8M15 9v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <circle cx="5" cy="3" r="1.5" fill="currentColor" />
                <circle cx="10" cy="6" r="1.5" fill="currentColor" />
                <circle cx="15" cy="9" r="1.5" fill="currentColor" />
            </svg>

    },
    {
        title: 'Cross-branch Comparison',
        desc: 'Compare code states across branches at the block level — understand exactly what changed and why, without the noise.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 4v12M15 4v5m0 3v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M5 8c3 0 5 1 5 4M15 9c-2 0-5 0-5-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="5" cy="4" r="1.5" fill="currentColor" />
                <circle cx="15" cy="4" r="1.5" fill="currentColor" />
                <circle cx="5" cy="16" r="1.5" fill="currentColor" />
                <circle cx="15" cy="16" r="1.5" fill="currentColor" />
            </svg>

    },
    {
        title: 'Zero-friction Collaboration',
        desc: 'Team-based state sharing that doesn\'t require a learning curve. Works with your existing Git workflow, not against it.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="14" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M2 17c0-2.76 2.24-4 5-4s5 1.24 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M14 13c2 0 4 1 4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>

    },
    {
        title: 'Selective Rollback',
        desc: 'Undo a specific change without reverting your entire commit history. Surgical precision for complex codebases.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 10a5 5 0 11-10 0 5 5 0 0110 0z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10 7v3l2 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

    },
    {
        title: 'IDE Integration',
        desc: 'Native integrations with VS Code, JetBrains, and Neovim. CapState lives where you already code.',
        icon:
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M6 7l-4 3 4 3M14 7l4 3-4 3M11 5l-2 10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

    }];


export default function CapStateDetail() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.1 }
        );
        if (sectionRef?.current) observer?.observe(sectionRef?.current);
        return () => observer?.disconnect();
    }, []);

    return (
        <section id="capstate" ref={sectionRef} className="py-24 px-6 md:px-10 border-b border-border opacity-100"
            style={{ background: 'linear-gradient(180deg, var(--background) 0%, #0d0e18 100%)' }}>
            <div className="max-w-7xl mx-auto">
                {/* Product header */}
                <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
                    {/* Image first on this one for visual variety */}
                    <div className={`relative rounded-2xl overflow-hidden h-80 glass-card order-2 lg:order-1 ${visible ? 'animate-fade-up' : 'opacity-0'}`}>
                        <AppImage
                            src="https://img.rocket.new/generatedImages/rocket_gen_img_17ec421bf-1772186759404.png"
                            alt="Multiple code files open in dark IDE, dim blue monitor glow, deep shadow background, focused developer workspace at night"
                            fill
                            className="object-cover" />

                        <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-card/30 to-transparent" />
                        {/* Floating code snippet */}
                        <div className="absolute bottom-6 right-6 glass-card px-4 py-3 rounded-xl font-mono text-xs">
                            <span className="text-[var(--capstate)]">restore</span>
                            <span className="text-muted-foreground"> --block </span>
                            <span className="text-primary">parseUser</span>
                            <span className="text-muted-foreground"> @v2.4</span>
                        </div>
                    </div>

                    <div className="order-1 lg:order-2">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-3 h-3 rounded-full bg-[var(--capstate)]" />
                            <span className="section-label text-[var(--capstate)]">Developer Tools</span>
                        </div>
                        <h2 className="text-hero-lg text-foreground mb-5">
                            Cap<span className="text-gradient-primary">State</span>
                        </h2>
                        <p className="text-xl text-muted-foreground leading-relaxed mb-6">
                            Rethinking version control by giving developers the ability to restore the code they actually need — not entire files.
                        </p>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                            Git was built for a different era. CapState layers intelligent, granular state management on top of your existing workflow — so when something breaks, you restore exactly what you need, in seconds, without the archaeology.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link href="https://capstate.codigo.com.ng" target='_blank' className="btn-primary" style={{ background: 'var(--capstate)', boxShadow: '0 0 24px rgba(124,58,237,0.3)' }}>
                                Explore CapState
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                            <Link href="https://github.com/Codigo-Ng/Cap-state-ext/blob/main/README.md" target='_blank' className="btn-secondary">
                                View documentation
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Features bento */}
                <div>
                    <p className="section-label text-muted-foreground mb-8">Platform capabilities</p>

                    {/* BENTO GRID AUDIT:
                    Array has 6 cards: [GranularRestore, FunctionHistory, CrossBranch, ZeroFriction, SelectiveRollback, IDEIntegration]
                    Row 1: [col-1: GranularRestore cs-1] [col-2: FunctionHistory cs-1] [col-3: CrossBranch cs-2]
                    Row 2: [col-1: ZeroFriction cs-2] [col-3: SelectiveRollback cs-1] [col-4: IDEIntegration cs-1]
                    Placed 6/6 cards ✓
                    */}
                    <div className="grid md:grid-cols-4 gap-4">
                        {/* Card 1 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-accent/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--capstate)]/10 text-[var(--capstate)] flex items-center justify-center mb-5 group-hover:bg-[var(--capstate)]/20 transition-colors">
                                {capstateFeatures?.[0]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{capstateFeatures?.[0]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{capstateFeatures?.[0]?.desc}</p>
                        </div>

                        {/* Card 2 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-accent/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '80ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--capstate)]/10 text-[var(--capstate)] flex items-center justify-center mb-5 group-hover:bg-[var(--capstate)]/20 transition-colors">
                                {capstateFeatures?.[1]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{capstateFeatures?.[1]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{capstateFeatures?.[1]?.desc}</p>
                        </div>

                        {/* Card 3 — wide */}
                        <div className={`md:col-span-2 glass-card p-7 rounded-2xl group hover:border-accent/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '160ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--capstate)]/10 text-[var(--capstate)] flex items-center justify-center mb-5 group-hover:bg-[var(--capstate)]/20 transition-colors">
                                {capstateFeatures?.[2]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{capstateFeatures?.[2]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{capstateFeatures?.[2]?.desc}</p>
                        </div>

                        {/* Card 4 — wide */}
                        <div className={`md:col-span-2 glass-card p-7 rounded-2xl group hover:border-accent/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '240ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--capstate)]/10 text-[var(--capstate)] flex items-center justify-center mb-5 group-hover:bg-[var(--capstate)]/20 transition-colors">
                                {capstateFeatures?.[3]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{capstateFeatures?.[3]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{capstateFeatures?.[3]?.desc}</p>
                        </div>

                        {/* Card 5 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-accent/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '320ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--capstate)]/10 text-[var(--capstate)] flex items-center justify-center mb-5 group-hover:bg-[var(--capstate)]/20 transition-colors">
                                {capstateFeatures?.[4]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{capstateFeatures?.[4]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{capstateFeatures?.[4]?.desc}</p>
                        </div>

                        {/* Card 6 */}
                        <div className={`glass-card p-7 rounded-2xl group hover:border-accent/30 transition-all duration-300 card-hover-lift ${visible ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '400ms' }}>
                            <div className="w-10 h-10 rounded-xl bg-[var(--capstate)]/10 text-[var(--capstate)] flex items-center justify-center mb-5 group-hover:bg-[var(--capstate)]/20 transition-colors">
                                {capstateFeatures?.[5]?.icon}
                            </div>
                            <h4 className="text-base font-700 text-foreground mb-2 tracking-tight">{capstateFeatures?.[5]?.title}</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">{capstateFeatures?.[5]?.desc}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}